import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import MoreVert from "@mui/icons-material/MoreVert";
import { handleCloneAssessment, handleUpdateAssessment } from "../services";
import classNames from "classnames";
import { STATIC_STRINGS } from "../../../utils/Constants";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

const RenderTable = ({
  searchTerm,
  data,
  setFetchOptions,
  updateAssessmentId,
}: any) => {
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filteredAssessments = data?.assessments?.filter((assessment) => {
    if (!assessment?.name) return false;

    const lowerCaseName = assessment.name.toLowerCase().trim();
    const searchWords = searchTerm.toLowerCase().trim().split(/\s+/);

    return searchWords.every((word) => lowerCaseName.includes(word));
  });

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    id: string
  ) => {
    setMenuAnchor(event.currentTarget);
    setSelectedId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedId(null);
  };

  const handleAssessmentClick = (assessment: any) => {
    if (assessment.status == STATIC_STRINGS.STATUS.DRAFT) {
      navigate(`/edit/${assessment.id}`);
    } else {
      navigate(`/report/${assessment.id}`);
    }
  };

  const getStatusClass = (status: string) => {
    const staticStatus = STATIC_STRINGS.STATUS;

    switch (status) {
      case staticStatus.ACTIVE:
        return "active";

      case staticStatus.DRAFT:
        return "draft";

      case staticStatus.PAUSED:
        return "paused";

      default:
        return "";
    }
  };

  return (
    <TableContainer>
      <Table>
        <TableHead className="bg-gray-200">
          <TableRow>
            <TableCell>
              {STATIC_STRINGS.TABLE_HEADERS.ASSESSMENT_NAME}
            </TableCell>
            <TableCell>{STATIC_STRINGS.TABLE_HEADERS.JOB_ROLE}</TableCell>
            <TableCell>{STATIC_STRINGS.TABLE_HEADERS.EXPERIENCE}</TableCell>
            <TableCell>{STATIC_STRINGS.TABLE_HEADERS.CANDIDATES}</TableCell>
            <TableCell sx={{ width: "125px" }}>
              {STATIC_STRINGS.TABLE_HEADERS.DEADLINE}
            </TableCell>
            <TableCell>
              <div className="flex justify-center w-full">
                {STATIC_STRINGS.TABLE_HEADERS.STATUS}
              </div>
            </TableCell>
            <TableCell sx={{ width: "125px" }}>
              {STATIC_STRINGS.TABLE_HEADERS.DATE_CREATED}
            </TableCell>
            <TableCell>
              <div className="flex justify-center w-full">
                {STATIC_STRINGS.TABLE_HEADERS.ACTIONS}
              </div>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredAssessments.map((assessment, idx) => (
            <TableRow
              key={assessment.id}
              onClick={(event) => {
                handleAssessmentClick(assessment);
              }}
            >
              <TableCell
                sx={{
                  borderBottom:
                    idx == filteredAssessments.length - 1 ? "none" : "",
                }}
              >
                {assessment.name}
              </TableCell>
              <TableCell
                sx={{
                  borderBottom:
                    idx == filteredAssessments.length - 1 ? "none" : "",
                }}
              >
                {assessment.jobRole || STATIC_STRINGS.N_A}
              </TableCell>
              <TableCell
                sx={{
                  borderBottom:
                    idx == filteredAssessments.length - 1 ? "none" : "",
                }}
              >
                {assessment.yearsOfExperience.min} -{" "}
                {assessment.yearsOfExperience.max} yrs
              </TableCell>
              <TableCell
                sx={{
                  borderBottom:
                    idx == filteredAssessments.length - 1 ? "none" : "",
                }}
              >
                {assessment.usercount}
              </TableCell>
              <TableCell
                sx={{
                  borderBottom:
                    idx == filteredAssessments.length - 1 ? "none" : "",
                }}
              >
                {assessment.assessmentLinkActiveDuration?.endTime
                  ? new Date(
                      assessment.assessmentLinkActiveDuration.endTime
                    ).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : STATIC_STRINGS.N_A}
              </TableCell>

              <TableCell
                sx={{
                  borderBottom:
                    idx == filteredAssessments.length - 1 ? "none" : "",
                  textAlign: "center",
                }}
              >
                {assessment?.status && (
                  <span
                    className={classNames(getStatusClass(assessment.status))}
                  >
                    {assessment.status}
                  </span>
                )}
              </TableCell>
              <TableCell
                sx={{
                  borderBottom:
                    idx == filteredAssessments.length - 1 ? "none" : "",
                }}
              >
                {new Date(assessment.createdAt).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </TableCell>

              <TableCell
                sx={{
                  borderBottom:
                    idx == filteredAssessments.length - 1 ? "none" : "",
                }}
              >
                <div className="flex items-center justify-end w-full">
                  <button
                    disabled={assessment.status === STATIC_STRINGS.STATUS.DRAFT}
                    className={classNames(
                      "mr-2 btn btn-outline",
                      assessment.status === STATIC_STRINGS.STATUS.DRAFT &&
                        "btn-outline-disabled"
                    )}
                  >
                    {STATIC_STRINGS.VIEW}
                  </button>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuClick(e, assessment.id);
                    }}
                  >
                    <MoreVert />
                  </IconButton>
                  {selectedId === assessment.id && (
                    <Menu
                      anchorEl={menuAnchor}
                      open={Boolean(menuAnchor)}
                      onClose={(e: Event) => {
                        e.stopPropagation();
                        handleMenuClose();
                      }}
                    >
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/edit/${selectedId}`);
                        }}
                      >
                        {STATIC_STRINGS.MENU_ITEMS.EDIT}
                      </MenuItem>
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          updateAssessmentId(assessment.id);
                        }}
                      >
                        {STATIC_STRINGS.MENU_ITEMS.DELETE}
                      </MenuItem>
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCloneAssessment(assessment.id);
                          setFetchOptions({
                            requestId: new Date().getTime().toString(),
                          });
                        }}
                      >
                        {STATIC_STRINGS.MENU_ITEMS.CLONE}
                      </MenuItem>
                      {assessment.status === STATIC_STRINGS.STATUS.ACTIVE && (
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateAssessment(
                              assessment.id,
                              STATIC_STRINGS.STATUS.PAUSED
                            );
                            setFetchOptions({
                              requestId: new Date().getTime().toString(),
                            });
                          }}
                        >
                          {STATIC_STRINGS.MENU_ITEMS.PAUSED}
                        </MenuItem>
                      )}
                      {assessment.status === STATIC_STRINGS.STATUS.PAUSED && (
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateAssessment(
                              assessment.id,
                              STATIC_STRINGS.STATUS.ACTIVE
                            );
                            setFetchOptions({
                              requestId: new Date().getTime().toString(),
                            });
                          }}
                        >
                          {STATIC_STRINGS.MENU_ITEMS.MAKE_ACTIVE}
                        </MenuItem>
                      )}
                    </Menu>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RenderTable;
